import { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import './WeatherView.css';

const WEATHER_API_URL =
  'https://api.open-meteo.com/v1/forecast?latitude=51.8607411&longitude=4.3114719&timezone=Europe%2FAmsterdam&current=temperature_2m,wind_speed_10m,rain,snowfall&hourly=temperature_2m,relative_humidity_2m,wind_speed_10m,rain,snowfall';

function formatTime(isoString) {
  const d = new Date(isoString);
  return d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(isoString) {
  const d = new Date(isoString);
  return d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });
}

function RaindropIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <title>Regen</title>
      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0L12 2.69z" />
    </svg>
  );
}

function SnowflakeIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden>
      <title>Sneeuw</title>
      <path d="M12 2v20M2 12h20M5.64 5.64l12.72 12.72M18.36 5.64L5.64 18.36M12 6l1.5 1.5M12 6L10.5 7.5M12 18l1.5-1.5M12 18l-1.5-1.5M6 12l1.5-1.5M6 12l1.5 1.5M18 12l-1.5 1.5M18 12l-1.5-1.5" />
    </svg>
  );
}

/** Find hourly index whose time is the same hour (or nearest past) as currentTime */
function getCurrentHourIndex(hourlyTimes, currentTimeIso) {
  if (!hourlyTimes?.length || !currentTimeIso) return 0;
  const current = new Date(currentTimeIso);
  const currentHour = current.getTime();
  let best = 0;
  for (let i = 0; i < hourlyTimes.length; i++) {
    const t = new Date(hourlyTimes[i]).getTime();
    if (t <= currentHour) best = i;
    else break;
  }
  return best;
}

function WeatherView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sliderIndex, setSliderIndex] = useState(0);
  const stripRef = useRef(null);
  const itemRefs = useRef([]);
  const initialScrollDone = useRef(false);
  const isScrollingByUser = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchWeather() {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(WEATHER_API_URL);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Weer ophalen mislukt');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchWeather();
    const interval = setInterval(fetchWeather, 10 * 60 * 1000); // refresh every 10 min
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const current = data?.current;
  const units = data?.current_units || {};
  const hourly = data?.hourly;
  const hourlyCount = hourly ? Math.min(24, hourly.time?.length ?? 0) : 0;
  const currentHourIndex = getCurrentHourIndex(hourly?.time, current?.time);

  // Scroll strip so the item at index is centered (JS-controlled)
  const scrollToIndex = useCallback((index, behavior = 'smooth') => {
    const strip = stripRef.current;
    const item = itemRefs.current[index];
    if (!strip || !item) return;
    const scrollLeft =
      item.offsetLeft - strip.clientWidth / 2 + item.offsetWidth / 2;
    const maxScroll = strip.scrollWidth - strip.clientWidth;
    strip.scrollTo({
      left: Math.max(0, Math.min(scrollLeft, maxScroll)),
      behavior,
    });
  }, []);

  // On first load: center current hour (instant). On index change: center that item (smooth).
  useLayoutEffect(() => {
    if (hourlyCount === 0) return;
    isScrollingByUser.current = false;
    if (!initialScrollDone.current) {
      scrollToIndex(currentHourIndex, 'auto');
      initialScrollDone.current = true;
      setSliderIndex(currentHourIndex);
    } else {
      scrollToIndex(sliderIndex, 'smooth');
    }
  }, [sliderIndex, hourlyCount, currentHourIndex, scrollToIndex]);

  const goPrev = () => {
    setSliderIndex((i) => Math.max(0, i - 1));
  };
  const goNext = () => {
    setSliderIndex((i) => Math.min(hourlyCount - 1, i + 1));
  };

  // Sync slider index when user scrolls (find item closest to center)
  const handleStripScroll = () => {
    if (!isScrollingByUser.current) return;
    const strip = stripRef.current;
    if (!strip || itemRefs.current.length === 0) return;
    const center = strip.scrollLeft + strip.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < itemRefs.current.length; i++) {
      const el = itemRefs.current[i];
      if (!el) continue;
      const itemCenter = el.offsetLeft + el.offsetWidth / 2;
      const d = Math.abs(itemCenter - center);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    setSliderIndex(best);
  };

  const handleStripPointerDown = () => {
    isScrollingByUser.current = true;
  };

  if (loading && !data) {
    return (
      <div className="weather-view">
        <div className="weather-view-loading">
          <span className="weather-view-spinner" />
          <span>Weer laden...</span>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="weather-view">
        <div className="weather-view-error">⚠️ {error}</div>
      </div>
    );
  }

  return (
    <div className="weather-view">
      <h3 className="weather-view-title">L.T.V. De Hartel weerbericht</h3>

      <div className="weather-view-current">
        <div className="weather-view-current-main">
          <span className="weather-view-temp">
            {current?.temperature_2m != null ? Math.round(current.temperature_2m) : '—'}
            {units.temperature_2m ?? '°C'}
          </span>
          <span className="weather-view-stat weather-view-wind">
            <span className="weather-view-stat-label">Wind</span>
            {current?.wind_speed_10m != null ? Math.round(current.wind_speed_10m) : '—'}
            {units.wind_speed_10m ?? ' km/h'}
          </span>
          {(() => {
            const rainVal = hourlyCount > 0 ? hourly?.rain?.[currentHourIndex] : current?.rain;
            const snowVal = hourlyCount > 0 ? hourly?.snowfall?.[currentHourIndex] : current?.snowfall;
            const rain = rainVal != null && rainVal > 0;
            const snow = snowVal != null && snowVal > 0;
            if (!rain && !snow) return null;
            return (
              <>
                {rain && (
                  <span className="weather-view-stat weather-view-rain">
                    <span className="weather-view-stat-label">
                      <RaindropIcon className="weather-view-stat-icon" /> Regen
                    </span>
                    {rainVal} {data?.hourly_units?.rain ?? units.rain ?? 'mm'}
                  </span>
                )}
                {snow && (
                  <span className="weather-view-stat weather-view-snow">
                    <span className="weather-view-stat-label">
                      <SnowflakeIcon className="weather-view-stat-icon" /> Sneeuw
                    </span>
                    {snowVal} {data?.hourly_units?.snowfall ?? units.snowfall ?? 'cm'}
                  </span>
                )}
              </>
            );
          })()}
        </div>
        {current?.time && (
          <div className="weather-view-time">
            {formatDate(current.time)} · {formatTime(current.time)}
          </div>
        )}
      </div>

      {hourlyCount > 0 && (
        <div className="weather-view-hourly">
          <div className="weather-view-hourly-header">
            <span className="weather-view-hourly-label">Komende 24 uur</span>
            <div className="weather-view-hourly-controls">
              <button
                type="button"
                className="weather-view-slider-btn"
                onClick={goPrev}
                disabled={sliderIndex === 0}
                aria-label="Vorige uur"
              >
                ‹
              </button>
              <span className="weather-view-slider-position">
                {sliderIndex + 1} / {hourlyCount}
              </span>
              <button
                type="button"
                className="weather-view-slider-btn"
                onClick={goNext}
                disabled={sliderIndex === hourlyCount - 1}
                aria-label="Volgende uur"
              >
                ›
              </button>
            </div>
          </div>
          <section
            ref={stripRef}
            className="weather-view-hourly-strip"
            aria-label="Uurlijkse verwachting"
            onScroll={handleStripScroll}
            onPointerDown={handleStripPointerDown}
          >
            {Array.from({ length: hourlyCount }, (_, i) => {
              const isCurrent = i === currentHourIndex;
              const isSelected = i === sliderIndex;
              return (
                <div
                  key={hourly.time[i]}
                  ref={(el) => {
                    itemRefs.current[i] = el;
                  }}
                  className={`weather-view-hourly-item${isCurrent ? ' weather-view-hourly-item--current' : ''}${isSelected ? ' weather-view-hourly-item--selected' : ''}`}
                >
                  <span className="weather-view-hourly-time">
                    {formatTime(hourly.time[i])}
                  </span>
                  <span className="weather-view-hourly-temp">
                    {hourly.temperature_2m[i] != null
                      ? Math.round(hourly.temperature_2m[i])
                      : '—'}
                    °
                  </span>
                  <span className="weather-view-hourly-meta">
                    {hourly.relative_humidity_2m?.[i] != null
                      ? `${hourly.relative_humidity_2m[i]}%`
                      : ''}
                    {hourly.wind_speed_10m?.[i] != null
                      ? ` · ${Math.round(hourly.wind_speed_10m[i])} km/h`
                      : ''}
                  </span>
                  {(hourly.rain?.[i] > 0 || hourly.snowfall?.[i] > 0) && (
                    <span className="weather-view-hourly-precip">
                      {hourly.rain?.[i] > 0 && (
                        <span className="weather-view-precip-item" title="Regen">
                          <RaindropIcon className="weather-view-precip-icon weather-view-precip-icon--rain" />
                          {hourly.rain[i]} mm
                        </span>
                      )}
                      {hourly.rain?.[i] > 0 && hourly.snowfall?.[i] > 0 && ' '}
                      {hourly.snowfall?.[i] > 0 && (
                        <span className="weather-view-precip-item" title="Sneeuw">
                          <SnowflakeIcon className="weather-view-precip-icon weather-view-precip-icon--snow" />
                          {hourly.snowfall[i]} cm
                        </span>
                      )}
                    </span>
                  )}
                </div>
              );
            })}
          </section>
        </div>
      )}

      {error && data && (
        <div className="weather-view-refresh-error">Laatste data; vernieuwen mislukt.</div>
      )}
    </div>
  );
}

export default WeatherView;
